'use client';

import { Box, Container, Stack, Text, Button, Field, Dialog, Portal, Alert } from '@chakra-ui/react';
import React from 'react';
import { resetPasswordStyles } from './reset-password.styles';
import { CanLogoIcon } from '@/../public/assets/icons';
import { PasswordInput } from '@/components/ui/password-input';
import Image from 'next/image';
import { resetPasswordSchema, TResetPassword } from './resetPassword.validator';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { resetPassword } from '@/services/auth/auth.service';
import { useRouter } from 'next/navigation';

const ResetPasswordForm = ({ token }: { token: string }) => {
   const { push } = useRouter();
   const [isOpen, setIsOpen] = React.useState(false);
   const {
      register,
      handleSubmit,
      formState: { errors },
      setError,
   } = useForm<TResetPassword>({
      defaultValues: {
         password: '',
         confirmPassword: '',
      },
      resolver: zodResolver(resetPasswordSchema),
   });

   const onSubmit = handleSubmit(async values => {
      const [error] = await resetPassword({ ...values, token });

      if (error) {
         setError('root', { message: error.response?.data.message as string, type: 'manual' });
         return;
      }

      setIsOpen(true);
   });

   const handleCloseDialog = ({ open }: Dialog.OpenChangeDetails) => {
      push('/auth/login');
      setIsOpen(open);
   };
   return (
      <Container css={resetPasswordStyles.container}>
         <Box css={resetPasswordStyles.reset_area}>
            <Box css={resetPasswordStyles.reset_box}>
               <CanLogoIcon />
               <Text as="h2" color="white" textStyle="4xl" fontWeight="bold">
                  Reset Password
               </Text>
               {errors.root?.message && (
                  <Alert.Root status="error">
                     <Alert.Indicator />
                     <Alert.Title>{errors.root.message}</Alert.Title>
                  </Alert.Root>
               )}
               <Box as="form" css={resetPasswordStyles.reset_form} onSubmit={onSubmit}>
                  <Stack gap="4" align="flex-start" maxW="sm">
                     <Field.Root invalid={!!errors.password}>
                        <Field.Label textStyle="xs" color="white" mb="1">
                           Password
                        </Field.Label>
                        <Box mb="2" w="full">
                           <PasswordInput
                              {...register('password')}
                              placeholder="Password"
                              size="md"
                              h="10"
                              rounded="12px"
                              bg="#202020"
                           />
                        </Box>
                        <Field.ErrorText>{errors.password?.message}</Field.ErrorText>
                     </Field.Root>

                     <Field.Root invalid={!!errors.confirmPassword}>
                        <Field.Label textStyle="xs" color="white" mb="1">
                           Repeat Password
                        </Field.Label>
                        <Box mb="2" w="full">
                           <PasswordInput
                              {...register('confirmPassword')}
                              placeholder="Repeat Password"
                              size="md"
                              h="10"
                              rounded="12px"
                              bg="#202020"
                           />
                        </Box>
                        <Field.ErrorText>{errors.confirmPassword?.message}</Field.ErrorText>
                     </Field.Root>
                     <Button type="submit" h="10" m="0 auto" mt="3">
                        Save
                     </Button>

                     <Dialog.Root
                        open={isOpen}
                        onOpenChange={handleCloseDialog}
                        placement="center"
                        motionPreset="slide-in-bottom"
                     >
                        <Portal>
                           <Dialog.Backdrop />
                           <Dialog.Positioner>
                              <Dialog.Content bg="#202020" rounded="15px" py="4" maxWidth="340px">
                                 <Dialog.Body>
                                    <Box
                                       w="16"
                                       h="16"
                                       rounded="50%"
                                       m="0 auto 10px"
                                       display="flex"
                                       alignItems="center"
                                       justifyContent="center"
                                    >
                                       <Image src="/assets/images/success_icon.svg" alt="" width="46" height="46" />
                                    </Box>
                                    <Dialog.Title color="white" textStyle="xl" textAlign="center" mb="2">
                                       Password Updated
                                    </Dialog.Title>
                                    <Text color="darkGray" textStyle="sm" textAlign="center">
                                       You have successfully reset your password, please log in to continue using your
                                       account
                                    </Text>
                                 </Dialog.Body>
                                 <Dialog.Footer justifyContent="center" pt="0">
                                    <Dialog.ActionTrigger asChild>
                                       <Button variant="outline" h="10">
                                          OK
                                       </Button>
                                    </Dialog.ActionTrigger>
                                 </Dialog.Footer>
                              </Dialog.Content>
                           </Dialog.Positioner>
                        </Portal>
                     </Dialog.Root>
                  </Stack>
               </Box>
            </Box>
         </Box>
      </Container>
   );
};

export default ResetPasswordForm;
